import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser notifications for Conversations.
 *
 * Two rules shape this:
 *
 *   1. Permission is only ever requested from a user gesture (opening the
 *      panel). Asking on page load is what trains people to click Block, after
 *      which the feature is unrecoverable without digging through site settings.
 *
 *   2. Nothing fires while the tab is visible — the member can already see the
 *      message arrive, and a notification for something on screen is pure noise.
 *      This mirrors the server-side `isActivelyViewing` check in
 *      notificationDecider.js, so in-app and out-of-app stay consistent.
 */

const SUPPORTED = typeof window !== 'undefined' && 'Notification' in window;

export function useDesktopNotifications() {
  const [permission, setPermission] = useState(SUPPORTED ? Notification.permission : 'unsupported');
  const openRef = useRef(new Map());

  // Close anything still on screen when the component unmounts, so a signed-out
  // member is not left with stale notifications referencing a dead session.
  useEffect(() => {
    const open = openRef.current;
    return () => {
      open.forEach((notification) => notification.close());
      open.clear();
    };
  }, []);

  const requestPermission = useCallback(async () => {
    if (!SUPPORTED) return 'unsupported';
    // 'denied' is terminal — calling requestPermission again is a silent no-op
    // in every browser, so don't pretend otherwise.
    if (Notification.permission !== 'default') {
      setPermission(Notification.permission);
      return Notification.permission;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return 'denied';
    }
  }, []);

  /**
   * @param {object} options
   * @param {string} options.title
   * @param {string} options.body
   * @param {string} options.tag    Collapses repeats — one notification per conversation.
   * @param {Function} options.onClick
   */
  const notify = useCallback(({ title, body, tag, onClick }) => {
    if (!SUPPORTED || Notification.permission !== 'granted') return null;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return null;

    try {
      const notification = new Notification(title, {
        body,
        tag,
        // Replaces a previous notification with the same tag rather than
        // stacking one per message in a busy conversation.
        renotify: false,
        silent: false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        openRef.current.delete(tag);
        onClick?.();
      };
      notification.onclose = () => openRef.current.delete(tag);

      openRef.current.set(tag, notification);
      return notification;
    } catch {
      // Some browsers throw when constructing a Notification outside a service
      // worker (notably Android Chrome). Failing silently is correct — this is
      // an enhancement, and email/push still cover the member.
      return null;
    }
  }, []);

  return { notify, requestPermission, permission, supported: SUPPORTED };
}

export default useDesktopNotifications;
